// In-page replacement for window.confirm / window.prompt. Embedded browsers and framed pages (the
// customer portal tab) can block native dialogs silently, which left the button doing nothing.
//   const [dialog, ask] = useConfirm();
//   const ok = await ask({ title, body, confirmLabel, cancelLabel, note, check });   // null = canceled, else { note, checked }
//   check: a checkbox label, unticked to start. body: text, or a node (a list). cancelLabel: null for a Close-only dialog.
//   ...render {dialog} somewhere in the component.
// A centered card on desktop, a bottom sheet with full-width buttons on a phone (styles.css, .dlg).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export function useConfirm() {
  const [req, setReq] = useState(null);
  const ask = useCallback((opts) => new Promise(resolve => setReq({ ...opts, resolve })), []);
  const close = (result) => { req?.resolve(result); setReq(null); };
  return [req ? <ConfirmDialog {...req} onClose={close} /> : null, ask];
}

function ConfirmDialog({ title, body, confirmLabel, cancelLabel = "Cancel", note, check, onClose }) {
  const [text, setText] = useState("");
  const [checked, setChecked] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    const before = document.activeElement;
    box.current.querySelector(note ? "textarea" : ".dlg-ok").focus();
    const onKey = (e) => {
      if (e.key === "Escape") return onClose(null);
      if (e.key !== "Tab") return;
      // Keep focus inside the dialog.
      const f = box.current.querySelectorAll("textarea, input, button, [tabindex]"), first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; before?.focus?.(); };
  }, []);

  return createPortal(
    <div className="dlg-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(null); }}>
      <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="dlg-title" ref={box}>
        <h2 id="dlg-title">{title}</h2>
        {body && (typeof body === "string" ? <p>{body}</p> : <div className="dlg-body">{body}</div>)}
        {note && <label>{note}<textarea rows={3} maxLength={500} value={text} onChange={e => setText(e.target.value)} /></label>}
        {check && <label className="dlg-check"><input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} />{check}</label>}
        <div className="dlg-actions">
          {cancelLabel !== null && <button type="button" className="dlg-cancel" onClick={() => onClose(null)}>{cancelLabel}</button>}
          <button type="button" className="dlg-ok" onClick={() => onClose({ note: text.trim(), checked })}>{confirmLabel}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
